/**
 * Typed library-config API helpers — Track 2.C.
 *
 * Wraps the oRPC `libraryConfig.*` procedures exposed by `apps/api` with
 * convenience functions consumed by the minimal editor UI
 * (`apps/web/src/features/library-config/`).
 *
 * Pattern follows `packages/client-core/src/api/collections.ts`:
 *   - Defines a structural interface (`LibraryConfigApiSurface`) covering only
 *     the `libraryConfig.*` oRPC procedures this client needs.
 *   - Avoids importing `ApiClient` directly to sidestep the pre-existing oRPC
 *     router type mismatch (see collections.ts header comment for full context).
 *   - Any real `ApiClient` instance satisfies the structural interface at
 *     runtime because the oRPC dynamic proxy forwards calls regardless of TS
 *     errors in the router type.
 *
 * Architecture refs:
 *   architecture.md §4.7  — `library_config` DDL
 *   extensibility-design §6.2 — MVP schema v1
 *   extensibility-design §6.3 — forward-compat: unknown keys preserved
 *   extensibility-design §6.4 — two-pass validation; errors carry line numbers
 *   Track 2.C — .cookrc.yml parse + validate + persist
 */

// ── LibraryConfig API surface ─────────────────────────────────────────────────
//
// Structural interface covering the `libraryConfig.*` oRPC procedures.
// Any ApiClient instance satisfies this at runtime.

/** A validation error with line-number information returned by the server. */
export interface LibraryConfigValidationError {
  /** One-indexed line number in the source YAML. */
  line: number;
  /** One-indexed column number in the source YAML. */
  col: number;
  /** Human-readable error message. */
  message: string;
}

/** Structural interface for the `libraryConfig.*` oRPC namespace. */
export interface LibraryConfigApiSurface {
  libraryConfig: {
    get(input?: Record<string, never>): Promise<unknown>;
    put(input: { configYaml: string }): Promise<unknown>;
  };
}

// ── Response types ────────────────────────────────────────────────────────────

/**
 * The `library_config` row returned by the GET and PUT procedures.
 * Mirrors `LibraryConfigRow` from `@proof/domain/cookrc` (schema.ts).
 */
export interface LibraryConfigDto {
  library_uuid: string;
  /** Raw YAML — authoritative for export and the editor textarea. */
  config_yaml: string;
  /** JSON-serialised `CookrcConfig` — consumed by the evaluator + editor UI. */
  config_json: string;
  /** Schema version from the YAML (`version: 1` at MVP). */
  version: number;
  updated_at: number;
}

/** Successful GET / PUT response. */
export type LibraryConfigGetResult = { row: LibraryConfigDto };

/**
 * Result of a PUT that failed validation.
 * The caller surfaces `errors` as inline line-number annotations.
 */
export interface LibraryConfigPutError {
  ok: false;
  /** Array of `{ line, col, message }` tuples from the two-pass validator. */
  validationErrors: LibraryConfigValidationError[];
  /** Human-readable summary, e.g. "validation_failed". */
  message: string;
}

// ── Client factory ────────────────────────────────────────────────────────────

/**
 * Create a typed library-config API client bound to an existing API client
 * instance.
 *
 * The parameter type is `LibraryConfigApiSurface` — a structural interface that
 * covers only the `libraryConfig.*` oRPC procedures this client needs. Any real
 * `ApiClient` instance satisfies this interface at runtime.
 *
 * Usage:
 *   const api = createApiClient({ baseUrl: "https://proofcook.com" });
 *   const cfg = createLibraryConfigClient(api);
 *   const { row } = await cfg.getLibraryConfig();
 *   await cfg.putLibraryConfig("version: 1\ndensities:\n  bread flour: 0.60\n");
 */
export function createLibraryConfigClient(api: LibraryConfigApiSurface) {
  return {
    /**
     * Fetch the current `.cookrc.yml` config for the authenticated user.
     *
     * If the user has never saved a config, the server returns a default-empty
     * row (`version: 1`, minimal YAML) created lazily on first access.
     *
     * Returns `{ row: LibraryConfigDto }`.
     */
    async getLibraryConfig(): Promise<LibraryConfigGetResult> {
      const result = await api.libraryConfig.get({});
      return result as LibraryConfigGetResult;
    },

    /**
     * Validate and persist a new `.cookrc.yml` YAML string.
     *
     * On success: returns the updated `{ row: LibraryConfigDto }`.
     *
     * On validation failure: the oRPC layer throws with `cause` set to an
     * array of `{ line, col, message }` error objects. This function re-throws
     * the error — callers should catch it and inspect `.cause` / `.validationErrors`
     * to display inline annotations.
     *
     * `author_agent` is server-stamped — the client MUST NOT include it in
     * the YAML or in any request field (architecture.md §4.23).
     *
     * @param yamlString — The raw `.cookrc.yml` text. Max 64 kB.
     * @throws When the server returns a validation error or network failure.
     */
    async putLibraryConfig(yamlString: string): Promise<LibraryConfigGetResult> {
      const result = await api.libraryConfig.put({ configYaml: yamlString });
      return result as LibraryConfigGetResult;
    },
  };
}

export type LibraryConfigClient = ReturnType<typeof createLibraryConfigClient>;
